import { makeAutoObservable, runInAction } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";

interface SafetyIncident {
  id?: number;
  jobId: number | null;
  reportedBy: number | null;
  reportDate: Date;
  incidentDate: Date;
  location: string;
  incidentType: "injury" | "near_miss" | "property_damage" | "environmental" | "security";
  severity: "low" | "medium" | "high" | "critical";
  description: string;
  injuredPersons: {
    name: string;
    position: string;
    company: string;
    injuryType: string;
    bodyPart: string;
    medicalAttention: boolean;
  }[];
  witnessNames: string[];
  immediateActions: string;
  rootCause: string;
  correctiveActions: string;
  preventiveActions: string;
  photos: File[];
  followUpRequired: boolean;
  followUpDate: Date | null;
  reportedToAuthorities: boolean;
  authorities: string[];
  status: "open" | "investigating" | "closed";
}

class SafetyComplianceWizardStore {
  rootStore: RootStore;

  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  incidentData: SafetyIncident = {
    jobId: null,
    reportedBy: null,
    reportDate: new Date(),
    incidentDate: new Date(),
    location: "",
    incidentType: "near_miss",
    severity: "low",
    description: "",
    injuredPersons: [],
    witnessNames: [],
    immediateActions: "",
    rootCause: "",
    correctiveActions: "",
    preventiveActions: "",
    photos: [],
    followUpRequired: false,
    followUpDate: null,
    reportedToAuthorities: false,
    authorities: [],
    status: "open",
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    this.activeStep--;
  };

  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  updateIncidentData = <K extends keyof SafetyIncident>(
    field: K,
    value: SafetyIncident[K]
  ) => {
    this.incidentData[field] = value;
  };

  addInjuredPerson = (person: SafetyIncident["injuredPersons"][0]) => {
    this.incidentData.injuredPersons = [...this.incidentData.injuredPersons, person];
  };

  removeInjuredPerson = (index: number) => {
    this.incidentData.injuredPersons = this.incidentData.injuredPersons.filter((_, i) => i !== index);
  };

  addWitness = (name: string) => {
    if (name && !this.incidentData.witnessNames.includes(name)) {
      this.incidentData.witnessNames = [...this.incidentData.witnessNames, name];
    }
  };

  removeWitness = (index: number) => {
    this.incidentData.witnessNames = this.incidentData.witnessNames.filter((_, i) => i !== index);
  };

  addPhotos = (files: FileList) => {
    const newFiles = Array.from(files).filter(file => file.type.startsWith("image/"));
    this.incidentData.photos = [...this.incidentData.photos, ...newFiles];
  };

  removePhoto = (index: number) => {
    this.incidentData.photos = this.incidentData.photos.filter((_, i) => i !== index);
  };

  validateCurrentStep = (): boolean => {
    this.setError(null);

    switch (this.activeStep) {
      case 0: // Basic Information
        if (!this.incidentData.jobId) {
          this.setError("Please select a job");
          return false;
        }
        if (!this.incidentData.reportedBy) {
          this.setError("Please select who is reporting this incident");
          return false;
        }
        if (!this.incidentData.description) {
          this.setError("Please provide incident description");
          return false;
        }
        break;

      case 1: // Incident Details
        if (!this.incidentData.location) {
          this.setError("Please specify incident location");
          return false;
        }
        if (this.incidentData.incidentType === "injury" && this.incidentData.injuredPersons.length === 0) {
          this.setError("Please add injured person details for injury incidents");
          return false;
        }
        break;

      case 2: // Analysis & Actions
        if (!this.incidentData.immediateActions) {
          this.setError("Please describe immediate actions taken");
          return false;
        }
        if (!this.incidentData.rootCause) {
          this.setError("Please identify root cause");
          return false;
        }
        if (!this.incidentData.correctiveActions) {
          this.setError("Please specify corrective actions");
          return false;
        }
        break;
    }

    return true;
  };

  submitIncident = async () => {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.setLoading(true);
    this.setError(null);

    try {
      // TODO: Create safety incident record when backend service is available
      // For now, just simulate success
      await new Promise(resolve => setTimeout(resolve, 1000));

      runInAction(() => {
        this.setSuccess("Safety incident report submitted successfully!");
        this.activeStep = 4; // Success step
      });

    } catch (err: any) {
      runInAction(() => {
        this.setError(err.message || "Failed to submit incident report");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.incidentData = {
      jobId: null,
      reportedBy: null,
      reportDate: new Date(),
      incidentDate: new Date(),
      location: "",
      incidentType: "near_miss",
      severity: "low",
      description: "",
      injuredPersons: [],
      witnessNames: [],
      immediateActions: "",
      rootCause: "",
      correctiveActions: "",
      preventiveActions: "",
      photos: [],
      followUpRequired: false,
      followUpDate: null,
      reportedToAuthorities: false,
      authorities: [],
      status: "open",
    };
  };

  // Computed values
  get jobs() {
    const jobStore = this.rootStore.jobStore as ServiceStore;
    return jobStore?.entities || [];
  }

  get users() {
    const userStore = this.rootStore.userStore as ServiceStore;
    return userStore?.entities || [];
  }

  get selectedJob() {
    if (!this.incidentData.jobId) return null;
    return this.jobs.find((j: any) => j.id === this.incidentData.jobId);
  }

  get hasInjuries() {
    return this.incidentData.incidentType === "injury" || this.incidentData.injuredPersons.length > 0;
  }

  get requiresAuthorityReport() {
    return this.incidentData.severity === "critical" || this.hasInjuries;
  }
}

export default SafetyComplianceWizardStore;